/**
 * SPA hooks for Phase 1.75 pricing-sheet ingestion.
 *
 * Pass 1 exposes: list, detail, upload (multipart), soft-delete.
 * Passes 2–4 add: row queries, decide / bulk-decide, manual-match, SSE.
 */
import { useMutation, useQuery, useQueryClient, queryOptions } from '@tanstack/react-query';
import type {
  BulkDecideRowsRequest,
  BulkDecideRowsResponse,
  CreatePricingSheetResponse,
  ListPricingSheetRowsResponse,
  ListPricingSheetsResponse,
  ManualMatchRowRequest,
  PricingSheetDetailResponse,
  PricingSheetRow,
  PricingRowDecision,
} from '@sourcito/schemas';
import { apiFetch, ApiCallError } from './api';

export interface ListPricingSheetsParams {
  supplierId?: string;
  status?: string;
  limit?: number;
}

function buildQueryString(params: ListPricingSheetsParams): string {
  const search = new URLSearchParams();
  if (params.supplierId) search.set('supplierId', params.supplierId);
  if (params.status) search.set('status', params.status);
  if (params.limit) search.set('limit', String(params.limit));
  const s = search.toString();
  return s ? `?${s}` : '';
}

export function pricingSheetsQuery(params: ListPricingSheetsParams = {}) {
  return queryOptions({
    queryKey: ['pricing-sheets', params] as const,
    queryFn: () =>
      apiFetch<ListPricingSheetsResponse>(`/api/pricing-sheets${buildQueryString(params)}`),
    staleTime: 10_000,
  });
}

export function pricingSheetQuery(id: string) {
  return queryOptions({
    queryKey: ['pricing-sheet', id] as const,
    queryFn: () => apiFetch<PricingSheetDetailResponse>(`/api/pricing-sheets/${id}`),
    staleTime: 5_000,
  });
}

export function usePricingSheets(params: ListPricingSheetsParams = {}) {
  return useQuery(pricingSheetsQuery(params));
}

export function usePricingSheet(id: string) {
  return useQuery(pricingSheetQuery(id));
}

export interface UploadPricingSheetInput {
  supplierId: string;
  file: File;
}

/**
 * Multipart upload — bypasses the apiFetch helper because that one auto-sets
 * Content-Type: application/json, which would break the boundary header that
 * the browser injects for FormData.
 */
export function useUploadPricingSheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UploadPricingSheetInput): Promise<CreatePricingSheetResponse> => {
      const fd = new FormData();
      fd.append('supplierId', input.supplierId);
      fd.append('file', input.file, input.file.name);
      const res = await fetch('/api/pricing-sheets', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const text = await res.text();
      const json: unknown = text ? JSON.parse(text) : null;
      if (!res.ok) {
        const message =
          (json as { error?: { message?: string } })?.error?.message ??
          `Upload failed with status ${res.status}`;
        throw new ApiCallError(res.status, message, json);
      }
      return json as CreatePricingSheetResponse;
    },
    onSuccess: (sheet) => {
      qc.invalidateQueries({ queryKey: ['pricing-sheets'] });
      qc.invalidateQueries({ queryKey: ['pricing-sheet', sheet.id] });
    },
  });
}

export function useDeletePricingSheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<null>(`/api/pricing-sheets/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pricing-sheets'] });
    },
  });
}

// ─────────────────────────────────────────────────────────
// Pass 4 — rows endpoints
// ─────────────────────────────────────────────────────────

export interface ListRowsParams {
  grade?: string;
  decision?: string;
  match?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

function buildRowsQueryString(params: ListRowsParams): string {
  const search = new URLSearchParams();
  if (params.grade) search.set('grade', params.grade);
  if (params.decision) search.set('decision', params.decision);
  if (params.match) search.set('match', params.match);
  if (params.q) search.set('q', params.q);
  if (params.limit) search.set('limit', String(params.limit));
  if (params.offset) search.set('offset', String(params.offset));
  const s = search.toString();
  return s ? `?${s}` : '';
}

export function pricingSheetRowsQuery(id: string, params: ListRowsParams = {}) {
  return queryOptions({
    queryKey: ['pricing-sheet-rows', id, params] as const,
    queryFn: () =>
      apiFetch<ListPricingSheetRowsResponse>(
        `/api/pricing-sheets/${id}/rows${buildRowsQueryString(params)}`
      ),
    staleTime: 5_000,
  });
}

export function usePricingSheetRows(id: string, params: ListRowsParams = {}) {
  return useQuery(pricingSheetRowsQuery(id, params));
}

export function useDecideRow(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      rowId,
      decision,
    }: {
      rowId: string;
      decision: PricingRowDecision;
    }): Promise<PricingSheetRow> =>
      apiFetch<PricingSheetRow>(`/api/pricing-sheets/${id}/rows/${rowId}`, {
        method: 'PATCH',
        body: JSON.stringify({ decision }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pricing-sheet-rows', id] });
      qc.invalidateQueries({ queryKey: ['pricing-sheet', id] });
    },
  });
}

export function useBulkDecide(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkDecideRowsRequest): Promise<BulkDecideRowsResponse> =>
      apiFetch<BulkDecideRowsResponse>(`/api/pricing-sheets/${id}/rows/bulk-decide`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pricing-sheet-rows', id] });
      qc.invalidateQueries({ queryKey: ['pricing-sheet', id] });
    },
  });
}

export function useManualMatchRow(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rowId, input }: { rowId: string; input: ManualMatchRowRequest }) =>
      apiFetch<null>(`/api/pricing-sheets/${id}/rows/${rowId}/match`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pricing-sheet-rows', id] });
    },
  });
}

export function useMarkSheetReviewed(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<PricingSheetDetailResponse>(`/api/pricing-sheets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'reviewed' }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pricing-sheet', id] });
      qc.invalidateQueries({ queryKey: ['pricing-sheets'] });
    },
  });
}
